import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  bridgeKeyboardFocus,
  Button,
  Icon,
  Text,
} from "@salestrak/ui";
import { useActivityContext } from "./activity-context";
import { NoteItem } from "./note-item";
import { NoteComposer } from "./note-composer";
import { useDockedComposer } from "./docked-composer-context";
import type { ActivityAuthor } from "./activity-types";
import { useIssueViewCapabilities } from "../issue-view-capabilities";
import type { Omittable } from "@salestrak/ui";

type NoteThreadProps = Omittable<{
  issueId: string;
  noteId: string;
  headerExtra?: React.ReactNode;
}>;

export function NoteThread({ issueId, noteId, headerExtra }: NoteThreadProps) {
  const { getNote, selectedParentId, landOnUnread, firstUnreadParentId } =
    useActivityContext("NoteThread");
  const note = getNote(noteId);
  const [collapsed, setCollapsed] = useState(true);
  // View-only viewers can't post replies, so the composer slot
  // collapses to nothing and we drop its wrapping padding too.
  const { canComment } = useIssueViewCapabilities();

  // A focused reply, or the landing's first unread when it is a reply, opens
  // the thread so the entry can be seen (and so marked read). The landing
  // opens it ONCE: its target stays set for the whole visit, so without the
  // guard every Hide would be undone on the next render.
  const openedForLandingRef = useRef(false);
  useEffect(() => {
    const focused = selectedParentId === noteId;
    const landing =
      landOnUnread &&
      firstUnreadParentId === noteId &&
      !openedForLandingRef.current;
    if ((focused || landing) && collapsed) {
      if (landing) openedForLandingRef.current = true;
      setCollapsed(false);
    }
  }, [selectedParentId, landOnUnread, firstUnreadParentId, noteId, collapsed]);

  const replyInfo = useMemo(() => {
    if (!collapsed || !note) return null;

    const replies = note.replies ?? [];
    const uniqueAuthors = new Map<string, ActivityAuthor>();

    for (const reply of replies) {
      if (reply.createdBy && reply.author && !uniqueAuthors.has(reply.createdBy)) {
        uniqueAuthors.set(reply.createdBy, reply.author);
      }
    }

    return {
      count: note.replyCount ?? replies.length,
      // Live, not the open-time snapshot: a collapsed reply cannot be seen,
      // so this count only drops when the thread opens and hides the row.
      unreadCount: replies.filter((r) => r.unread).length,
      authors: Array.from(uniqueAuthors.values()),
    };
  }, [collapsed, note]);

  const handleExpandThread = useCallback(() => {
    setCollapsed(false);
  }, []);

  // In the phone sheet a reply is written in the bar on the keyboard; the
  // thread opens to show what is being answered and keeps no composer.
  const dock = useDockedComposer();
  const authorName = note?.author?.name ?? null;

  // Bumped per Reply press so the composer focuses even when already open.
  const [replyFocus, setReplyFocus] = useState(0);
  const handleReplyRequest = useCallback(() => {
    setCollapsed(false);
    if (dock) {
      dock.reply(noteId, authorName);
      return;
    }
    bridgeKeyboardFocus();
    setReplyFocus((n) => n + 1);
  }, [dock, noteId, authorName]);

  if (!note) return null;

  const replies = note.replies ?? [];

  return (
    <Box marginBottom="md">
      <NoteItem
        issueId={issueId}
        noteId={noteId}
        isParent
        headerExtra={headerExtra}
        onRequestReply={handleReplyRequest}
        onExpandThread={handleExpandThread}
        replyInfo={replyInfo}
        showFooterBorder={collapsed}
      />

      {!collapsed && (
        <Box
          border={["bottom", "left", "right"]}
          // A continuation of the parent note card, not a new card — fill
          // only, no second shadow.
          background="card"
          overflow="hidden"
          className="rounded-b-lg"
        >
          {replies.length > 0 && (
            <Box
              direction="column"
              gap="xs"
              paddingX="md"
              paddingTop="md"
              paddingBottom="sm"
            >
              {replies.map((reply) => (
                <NoteItem
                  key={reply.noteId}
                  issueId={issueId}
                  noteId={reply.noteId}
                  onRequestReply={handleReplyRequest}
                />
              ))}
            </Box>
          )}

          {canComment && !dock && (
            <Box paddingX="md" paddingY="md">
              <NoteComposer issueId={issueId} parentNoteId={noteId} focusRequest={replyFocus} aria-label="Reply" />
            </Box>
          )}
          {/* The composer's seat in the sheet: the door to the bar. */}
          {canComment && dock && (
            <Box paddingX="md" paddingY="sm">
              <Button variant="ghost" size="sm" onClick={handleReplyRequest}>
                <Icon name="reply" />
                Reply
              </Button>
            </Box>
          )}

          <Box
            align="center"
            justify="center"
            paddingY="sm"
            paddingX="md"
            border="top"
            background="card"
          >
            <Button variant="link" size="sm" onClick={() => setCollapsed(true)}>
              <Text size="xs" variant="muted">
                Hide
              </Text>
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
